import { useState, useRef, DragEvent, ChangeEvent } from 'react';
import { useS3Upload } from '@/hooks/useS3Upload';

const MAX_SIZE_MB = 5;

interface ImageUploaderProps {
  currentUrl: string;
  onChange: (url: string) => void;
}

export default function ImageUploader({ currentUrl, onChange }: ImageUploaderProps) {
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  // キャッシュバスター用のタイムスタンプ
  const [cacheBuster, setCacheBuster] = useState<number>(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const { upload, uploading, error: uploadError, reset } = useS3Upload();

  const uploadFile = async (file: File) => {
    setValidationError(null);
    reset();

    if (!file.type.startsWith('image/')) {
      setValidationError('画像ファイルを選択してください（JPEG / PNG / WebP / GIF）');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setValidationError(`ファイルサイズは${MAX_SIZE_MB}MB以内にしてください。`);
      return;
    }

    setProgress(10);

    const progressInterval = setInterval(() => {
      setProgress((prev) => (prev < 80 ? prev + 10 : prev));
    }, 200);

    try {
      const result = await upload(file);
      clearInterval(progressInterval);
      setProgress(100);
      // キャッシュバスターを更新して画像を強制リフレッシュ
      setCacheBuster(Date.now());
      // setTimeoutを使わず即時反映（遅延させると新規作成時にstateが上書きされるバグがある）
      onChange(result.url);
      setProgress(0);
    } catch {
      clearInterval(progressInterval);
      setProgress(0);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleRemove = () => {
    onChange('');
    setValidationError(null);
    reset();
    setCacheBuster(0);
  };

  // キャッシュバスターをURLに付与（同一URLでも再取得させる）
  const getDisplayUrl = (url: string) => {
    if (!url) return url;
    if (cacheBuster === 0) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${cacheBuster}`;
  };

  const displayError = validationError || uploadError;

  return (
    <div className="flex flex-col gap-2">
      {/* Drop zone / preview */}
      {currentUrl ? (
        <div className="relative w-full h-44 rounded-xl overflow-hidden group" style={{ border: '1px solid #e8d8cc' }}>
          <img
            key={cacheBuster || currentUrl}
            src={getDisplayUrl(currentUrl)}
            alt="サムネイル"
            className="w-full h-full object-cover object-center"
          />
          {/* アップロード中オーバーレイ */}
          {uploading && (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8"
              style={{ background: 'rgba(0,0,0,0.55)' }}
            >
              <span className="flex items-center justify-center w-10 h-10">
                <i className="ri-upload-cloud-2-line text-3xl text-white"></i>
              </span>
              <p className="text-sm font-bold text-white">アップロード中...</p>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.3)' }}>
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${progress}%`, background: '#5a9e6f' }}
                />
              </div>
              <p className="text-xs text-white">{progress}%</p>
            </div>
          )}
          {/* ホバーオーバーレイ（アップロード中は非表示） */}
          {!uploading && (
            <div
              className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity"
              style={{ background: 'rgba(0,0,0,0.45)' }}
            >
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                style={{ background: '#5a9e6f' }}
              >
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-upload-2-line"></i>
                </span>
                差し替える
              </button>
              <button
                type="button"
                onClick={handleRemove}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
                style={{ background: '#cc4444' }}
              >
                <span className="flex items-center justify-center w-4 h-4">
                  <i className="ri-delete-bin-line"></i>
                </span>
                削除
              </button>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`w-full h-44 rounded-xl flex flex-col items-center justify-center gap-3 cursor-pointer transition-all ${
            dragOver ? 'scale-[1.01]' : ''
          }`}
          style={{
            border: `2px dashed ${dragOver ? '#5a9e6f' : '#d4c4ba'}`,
            background: dragOver ? '#f2f8f4' : '#fdfaf8',
          }}
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-3 w-full px-8">
              <span className="flex items-center justify-center w-10 h-10">
                <i className="ri-upload-cloud-2-line text-3xl" style={{ color: '#5a9e6f' }}></i>
              </span>
              <p className="text-sm font-bold" style={{ color: '#5a9e6f' }}>アップロード中...</p>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: '#e8d8cc' }}>
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${progress}%`, background: '#5a9e6f' }}
                />
              </div>
              <p className="text-xs" style={{ color: '#9b8b7f' }}>{progress}%</p>
            </div>
          ) : (
            <>
              <span className="flex items-center justify-center w-12 h-12 rounded-full" style={{ background: '#f0e8e0' }}>
                <i className="ri-image-add-line text-2xl" style={{ color: '#8b6b5a' }}></i>
              </span>
              <div className="text-center">
                <p className="text-sm font-bold mb-0.5" style={{ color: '#4a3728' }}>
                  クリックまたはドラッグ＆ドロップ
                </p>
                <p className="text-xs" style={{ color: '#9b8b7f' }}>
                  JPEG・PNG・WebP・GIF　最大{MAX_SIZE_MB}MB
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Error */}
      {displayError && (
        <p className="text-xs flex items-center gap-1" style={{ color: '#cc4444' }}>
          <span className="flex items-center justify-center w-3 h-3">
            <i className="ri-error-warning-line"></i>
          </span>
          {displayError}
        </p>
      )}

      {/* URL input fallback */}
      <div className="flex items-center gap-2">
        <div className="h-px flex-1" style={{ background: '#e8d8cc' }}></div>
        <span className="text-xs" style={{ color: '#b4a49a' }}>またはURLで指定</span>
        <div className="h-px flex-1" style={{ background: '#e8d8cc' }}></div>
      </div>
      <input
        type="text"
        value={currentUrl}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://..."
        className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all"
        style={{ border: '1px solid #d4c4ba', background: '#fff', color: '#4a3728' }}
      />

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}